/**
 * Tenant-wide Copilot Studio inventory (read only).
 */

import { z } from "zod";
import {
  COPILOT_INVENTORY_DEFAULT_LIMIT,
  COPILOT_INVENTORY_MAX_LIMIT,
  type CopilotAgentInventoryRecord,
  type CopilotInventorySharingCounts,
  type PowerPlatformInventoryApi,
} from "../api/power-platform-inventory-api.js";
import { GUID_PATTERN } from "../utils/security.js";

export const getCopilotAgentInventoryInputSchema = z
  .object({
    agentId: z
      .string()
      .regex(GUID_PATTERN, "agentId must be a GUID")
      .optional()
      .describe("Exact Copilot Studio agent GUID. Omit to list the visible tenant inventory."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(COPILOT_INVENTORY_MAX_LIMIT)
      .optional()
      .describe(
        `Maximum agents to return (default ${COPILOT_INVENTORY_DEFAULT_LIMIT}, maximum ${COPILOT_INVENTORY_MAX_LIMIT}).`,
      ),
    skipToken: z
      .string()
      .min(1)
      .max(8_192)
      .optional()
      .describe(
        "Opaque nextSkipToken from a previous inventory call. Do not combine with agentId.",
      ),
  })
  .refine((value) => !(value.agentId && value.skipToken), {
    message: "skipToken cannot be combined with agentId",
    path: ["skipToken"],
  });

export type GetCopilotAgentInventoryInput = z.infer<typeof getCopilotAgentInventoryInputSchema>;

export const getCopilotAgentInventoryTool = {
  name: "get_copilot_agent_inventory",
  description:
    "Read the official tenant-wide Power Platform inventory for Copilot Studio agents, or one agent by GUID. Unlike list_copilot_agents (current-environment Dataverse rows visible to the maker), this governance view spans environments and reports published configuration metadata: owner/creator IDs, model, orchestration, authentication, channels, sharing counts, quarantine/managed state, topic/tool/knowledge counts, capability counts, and connector operations. It warns when the inventory API's nondeterministic 200-item connector detail cap makes returned connector data incomplete. It is eventually consistent (normally up to about 20 minutes behind) and does not include unpublished draft changes. Requires Power Platform API ResourceQuery.Resources.Read plus a supported Microsoft Entra inventory role; AI Reader or Global Reader is sufficient. Read-only and bounded to 500 agents per call.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description:
          "Exact Copilot Studio agent GUID. Omit to list agents visible through tenant inventory.",
      },
      limit: {
        type: "integer",
        minimum: 1,
        maximum: COPILOT_INVENTORY_MAX_LIMIT,
        default: COPILOT_INVENTORY_DEFAULT_LIMIT,
        description: `Maximum agents to return (default ${COPILOT_INVENTORY_DEFAULT_LIMIT}, maximum ${COPILOT_INVENTORY_MAX_LIMIT}).`,
      },
      skipToken: {
        type: "string",
        minLength: 1,
        maxLength: 8_192,
        description:
          "Opaque nextSkipToken from a previous call. Use it to resume a truncated tenant listing; do not combine with agentId.",
      },
    },
    allOf: [
      {
        if: { required: ["agentId"] },
        then: { not: { required: ["skipToken"] } },
      },
    ],
  },
};

function yesNoUnknown(value: boolean | undefined): string {
  return value === undefined ? "unknown" : value ? "yes" : "no";
}

function formatSharing(counts: CopilotInventorySharingCounts | undefined): string {
  if (!counts) return "not reported";
  const parts = [
    counts.userCount === undefined
      ? "user count not reported"
      : `${counts.userCount} user${counts.userCount === 1 ? "" : "s"}`,
    counts.groupCount === undefined
      ? "group count not reported"
      : `${counts.groupCount} group${counts.groupCount === 1 ? "" : "s"}`,
  ];
  if (counts.entireTenant !== undefined) {
    parts.push(`entire tenant: ${counts.entireTenant ? "yes" : "no"}`);
  }
  return parts.join(", ");
}

function formatCounts(record: CopilotAgentInventoryRecord): string {
  const counts = record.componentsCounts;
  if (!counts) return "not reported";
  const parts: string[] = [];
  if (counts.topics !== undefined) parts.push(`${counts.topics} topics`);
  if (counts.tools !== undefined) parts.push(`${counts.tools} tools`);
  if (counts.knowledge !== undefined) parts.push(`${counts.knowledge} knowledge sources`);
  if (counts.connectedAgents !== undefined) {
    parts.push(`${counts.connectedAgents} connected agents`);
  }
  if (record.instructionsCharactersCount !== undefined) {
    parts.push(`${record.instructionsCharactersCount} instruction characters`);
  }
  return parts.length > 0 ? parts.join(", ") : "not reported";
}

function formatCapabilities(record: CopilotAgentInventoryRecord): string {
  const counts = record.capabilitiesCounts;
  if (!counts) return "not reported";
  const parts: string[] = [];
  if (counts.distinctPowerPlatformConnectors !== undefined) {
    parts.push(`${counts.distinctPowerPlatformConnectors} distinct connectors`);
  }
  if (counts.distinctPowerPlatformConnectorsOperations !== undefined) {
    parts.push(`${counts.distinctPowerPlatformConnectorsOperations} distinct connector operations`);
  }
  if (counts.flows !== undefined) parts.push(`${counts.flows} flows`);
  return parts.length > 0 ? parts.join(", ") : "not reported";
}

function connectorCompletenessWarning(record: CopilotAgentInventoryRecord): string | undefined {
  const counts = record.capabilitiesCounts;
  const returnedConnectors = record.powerPlatformConnectors.length;
  const returnedOperations = record.powerPlatformConnectors.reduce(
    (sum, connector) => sum + connector.operations.length,
    0,
  );
  const missingConnectors =
    counts?.distinctPowerPlatformConnectors !== undefined &&
    counts.distinctPowerPlatformConnectors > returnedConnectors;
  const missingOperations =
    counts?.distinctPowerPlatformConnectorsOperations !== undefined &&
    counts.distinctPowerPlatformConnectorsOperations > returnedOperations;
  if (!record.connectorDetailsIncomplete && !missingConnectors && !missingOperations) {
    return undefined;
  }
  const evidence =
    missingConnectors || missingOperations
      ? `capability counts report ${counts?.distinctPowerPlatformConnectors ?? "unknown"} connectors / ` +
        `${counts?.distinctPowerPlatformConnectorsOperations ?? "unknown"} operations, but the response ` +
        `contains ${returnedConnectors} / ${returnedOperations}`
      : "the raw connector response exceeded the local 200-item safety bounds";
  return (
    `  ⚠ Connector detail is incomplete: ${evidence}. Inventory returns a nondeterministic ` +
    "maximum of 200 resources per capability type."
  );
}

function summaryLine(record: CopilotAgentInventoryRecord): string {
  const published = record.lastPublishedAt
    ? `published ${record.lastPublishedAt}`
    : "draft/unpublished";
  const quarantine = record.isQuarantined ? ", QUARANTINED" : "";
  const summary =
    `• ${record.displayName ?? "Unnamed agent"} — ${record.agentId}\n` +
    `  Environment: ${record.environmentId ?? "not reported"}; ${published}${quarantine}\n` +
    `  ${record.orchestration ?? "unknown orchestration"}; model ${record.model ?? "not reported"}; ` +
    `authentication ${record.authentication ?? "not reported"}; channels ` +
    `${record.channels.length > 0 ? record.channels.join(", ") : "none reported"}\n` +
    `  Components: ${formatCounts(record)}\n` +
    `  Capabilities: ${formatCapabilities(record)}\n` +
    `  Owner: ${record.ownerId ?? "not reported"}; created by: ${record.createdBy ?? "not reported"}`;
  const incomplete = connectorCompletenessWarning(record);
  return incomplete ? `${summary}\n${incomplete}` : summary;
}

function detailLines(record: CopilotAgentInventoryRecord): string[] {
  const lines = [summaryLine(record)];
  lines.push(
    `  Schema: ${record.schemaName ?? "not reported"}; created in ${record.createdIn ?? "not reported"}`,
    `  Created: ${record.createdAt ?? "not reported"}; last published: ${record.lastPublishedAt ?? "never/not reported"}`,
    `  Managed solution: ${yesNoUnknown(record.isManaged)}; quarantined: ${yesNoUnknown(record.isQuarantined)}`,
    `  Viewers: ${formatSharing(record.sharedWithViewers)}`,
    `  Editors: ${formatSharing(record.sharedWithEditors)}`,
    `  Web search for knowledge: ${yesNoUnknown(record.isWebSearchEnabledForKnowledge)}`,
  );

  const connectors = record.powerPlatformConnectors;
  if (connectors.length === 0) {
    lines.push("  Connector operations: none reported");
  } else {
    lines.push("  Connector operations:");
    for (const connector of connectors) {
      if (connector.operations.length === 0) {
        lines.push(`    - ${connector.connectorId ?? "unknown connector"}: no operations reported`);
        continue;
      }
      for (const operation of connector.operations) {
        const state =
          operation.isEnabled === undefined
            ? "state unknown"
            : operation.isEnabled
              ? "enabled"
              : "disabled";
        const details = [
          state,
          operation.type,
          operation.usedAs,
          operation.whenCanBeUsed,
          operation.connectionProvider ? `connection: ${operation.connectionProvider}` : undefined,
          operation.requiresEndUserConsent === undefined
            ? undefined
            : `end-user consent: ${operation.requiresEndUserConsent ? "required" : "not required"}`,
        ].filter((value): value is string => value !== undefined);
        lines.push(
          `    - ${connector.connectorId ?? "unknown connector"} / ` +
            `${operation.operationId ?? "unknown operation"} (${details.join(", ")})`,
        );
      }
    }
  }
  return lines;
}

export async function handleGetCopilotAgentInventory(
  api: PowerPlatformInventoryApi,
  input: unknown,
): Promise<{ text: string; structured: Record<string, unknown> }> {
  const parsed = getCopilotAgentInventoryInputSchema.parse(input);
  const result = await api.queryCopilotAgents(parsed);

  const scope = parsed.agentId ? `agent ${parsed.agentId}` : "tenant";
  const visibleCount = result.totalRecords ?? result.count;
  const header =
    `Copilot Studio ${scope} inventory — ${result.count}` +
    (result.totalRecords !== undefined ? ` of ${result.totalRecords}` : "") +
    ` visible agent${visibleCount === 1 ? "" : "s"}`;
  const note =
    "Inventory reflects the published configuration and can lag a change by about 20 minutes; " +
    "use get_copilot_agent for the current Dataverse draft.";

  if (result.agents.length === 0) {
    const continuation = result.truncated
      ? result.nextSkipToken
        ? " More agents remain; pass nextSkipToken from structuredContent as skipToken to resume."
        : " The service reported a truncated result without a continuation token."
      : "";
    return {
      text: `${header}\n\nNo matching inventory record is visible. This does not prove the agent does not exist; check the agent ID and the signed-in user's tenant inventory role.${continuation}\n\n${note}`,
      structured: result as unknown as Record<string, unknown>,
    };
  }

  const lines = [header, note, ""];
  if (parsed.agentId) {
    lines.push(...detailLines(result.agents[0]!));
  } else {
    lines.push(...result.agents.map(summaryLine));
  }
  if (result.truncated) {
    lines.push(
      "",
      result.nextSkipToken
        ? "More agents remain. Pass nextSkipToken from structuredContent as skipToken to resume."
        : "The service reported a truncated result but did not provide a continuation token.",
    );
  }

  return {
    text: lines.join("\n"),
    structured: result as unknown as Record<string, unknown>,
  };
}
